
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AgentFormData } from "@/types/admin";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface AgentFormProps {
  agent: AgentFormData;
  onSubmit: (values: AgentFormData) => void;
  isUpdating?: boolean;
  hideSubmitButton?: boolean;
}

export const AgentForm = ({ agent, onSubmit, isUpdating, hideSubmitButton }: AgentFormProps) => {
  const { control, register, handleSubmit, setValue, formState: { errors } } = useFormContext<AgentFormData>();

  useEffect(() => {
    // Set default values when the agent prop changes
    setValue('name', agent.name);
    setValue('description', agent.description || '');
    setValue('voice_id', agent.voice_id);
    setValue('language', agent.language);
    setValue('first_message', agent.first_message || '');
    
    // Convert number values properly
    if (agent.max_duration_seconds !== null && agent.max_duration_seconds !== undefined) {
      setValue('max_duration_seconds', agent.max_duration_seconds);
    }
    setValue('goal', agent.goal || '');
    
    if (agent.stability !== null && agent.stability !== undefined) {
      setValue('stability', agent.stability);
    }
    
    if (agent.speed !== null && agent.speed !== undefined) {
      setValue('speed', agent.speed);
    }
    
    if (agent.similarity_boost !== null && agent.similarity_boost !== undefined) {
      setValue('similarity_boost', agent.similarity_boost);
    }
    
    setValue('optimize_latency', agent.optimize_latency !== null && agent.optimize_latency !== undefined ? agent.optimize_latency : true);
    setValue('elevenlabs_agent_id', agent.elevenlabs_agent_id || '');
    setValue('phone_number', agent.phone_number || '');
    setValue('phone_number_id', agent.phone_number_id || '');
  }, [agent, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="Agent Name" {...field} />
            </FormControl>
            <FormDescription>This is the name of the agent.</FormDescription>
            <FormMessage>{errors.name?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="A brief description of the agent"
                className="resize-none"
                {...field}
                value={field.value || ''}
              />
            </FormControl>
            <FormDescription>Describe the agent's purpose and capabilities.</FormDescription>
            <FormMessage>{errors.description?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voice_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Voice ID</FormLabel>
            <FormControl>
              <Input placeholder="Voice ID" {...field} />
            </FormControl>
            <FormDescription>The ID of the voice to use for the agent.</FormDescription>
            <FormMessage>{errors.voice_id?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="language"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Language</FormLabel>
            <FormControl>
              <Input placeholder="Language" {...field} />
            </FormControl>
            <FormDescription>The language the agent will speak.</FormDescription>
            <FormMessage>{errors.language?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="first_message"
        render={({ field }) => (
          <FormItem>
            <FormLabel>First Message</FormLabel>
            <FormControl>
              <Textarea
                placeholder="The first message the agent will say"
                className="resize-none"
                {...field}
                value={field.value || ''}
              />
            </FormControl>
            <FormDescription>The initial message the agent will use when starting a conversation.</FormDescription>
            <FormMessage>{errors.first_message?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="max_duration_seconds"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Max Duration (Seconds)</FormLabel>
            <FormControl>
              <Input
                placeholder="Max Duration in Seconds"
                type="number"
                {...field}
                onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
              />
            </FormControl>
            <FormDescription>The maximum duration of the conversation in seconds.</FormDescription>
            <FormMessage>{errors.max_duration_seconds?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="goal"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Goal</FormLabel>
            <FormControl>
              <Input placeholder="Goal" {...field} />
            </FormControl>
            <FormDescription>The goal of the agent.</FormDescription>
            <FormMessage>{errors.goal?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="stability"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Stability</FormLabel>
            <FormControl>
              <Input
                placeholder="Stability"
                type="number"
                step="0.01"
                {...field}
                onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
              />
            </FormControl>
            <FormDescription>The stability of the voice.</FormDescription>
            <FormMessage>{errors.stability?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="speed"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Speed</FormLabel>
            <FormControl>
              <Input
                placeholder="Speed"
                type="number"
                step="0.01"
                {...field}
                onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
              />
            </FormControl>
            <FormDescription>The speed of the voice.</FormDescription>
            <FormMessage>{errors.speed?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="similarity_boost"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Similarity Boost</FormLabel>
            <FormControl>
              <Input
                placeholder="Similarity Boost"
                type="number"
                step="0.01"
                {...field}
                onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
              />
            </FormControl>
            <FormDescription>The similarity boost of the voice.</FormDescription>
            <FormMessage>{errors.similarity_boost?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="optimize_latency"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Optimize Latency</FormLabel>
              <FormDescription>Whether to optimize latency for the agent's voice.</FormDescription>
            </div>
            <FormMessage>{errors.optimize_latency?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="elevenlabs_agent_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>ElevenLabs Agent ID</FormLabel>
            <FormControl>
              <Input placeholder="ElevenLabs Agent ID" {...field} />
            </FormControl>
            <FormDescription>The ID of the agent in ElevenLabs.</FormDescription>
            <FormMessage>{errors.elevenlabs_agent_id?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="phone_number"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Phone Number</FormLabel>
            <FormControl>
              <Input placeholder="Phone Number" {...field} />
            </FormControl>
            <FormDescription>The phone number associated with the agent.</FormDescription>
            <FormMessage>{errors.phone_number?.message}</FormMessage>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="phone_number_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Phone Number ID</FormLabel>
            <FormControl>
              <Input placeholder="Phone Number ID" {...field} />
            </FormControl>
            <FormDescription>The ID of the phone number in ElevenLabs.</FormDescription>
            <FormMessage>{errors.phone_number_id?.message}</FormMessage>
          </FormItem>
        )}
      />

      {!hideSubmitButton && (
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? "Updating..." : "Update Agent"}
        </Button>
      )}
    </form>
  );
};
